import styled from "styled-components";
import { motion } from "framer-motion";
import {
  Smartphone,
  Globe,
  Tv,
  Monitor,
  Watch,
  Cpu,
  Puzzle,
  Server,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

/* ================= PAGE ================= */

const Page = styled.section`
  padding: 140px 20px;
  background: ${({ theme }) => theme.colors.bg};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: auto;
`;

/* ================= HEADER ================= */

const Heading = styled.h1`
  text-align: center;
  font-size: clamp(38px, 5vw, 56px);
  font-weight: 900;
`;

const Sub = styled.p`
  max-width: 900px;
  margin: 24px auto 80px;
  text-align: center;
  font-size: 18px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ================= GRID ================= */

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 42px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* ================= CARD ================= */

const Card = styled(motion.div)`
  padding: 40px;
  border-radius: 28px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.035),
    rgba(255, 255, 255, 0.015)
  );
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 30px 80px rgba(2, 6, 23, 0.65);
`;

/* ================= HEADER ================= */

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 28px;
`;

const IconWrap = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  color: white;
  background: ${({ color }) => color};
`;

const Title = styled.h3`
  font-size: 22px;
  font-weight: 900;
`;

/* ================= SECTIONS ================= */

const Section = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.div`
  font-size: 13px;
  font-weight: 800;
  opacity: 0.7;
  margin-bottom: 10px;
  text-transform: uppercase;
`;

/* ================= TECH LIST ================= */

const TechList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

/* ================= TECH ITEM ================= */

const TechItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 12px;

  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);

  font-size: 14px;
  font-weight: 600;
`;

/* ================= LETTER SVG ICON ================= */

const TechIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 8px;
  display: grid;
  place-items: center;

  font-size: 12px;
  font-weight: 900;
  color: white;

  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.9),
    rgba(6, 182, 212, 0.9)
  );
`;
/* ================= CTA ================= */

const CTA = styled.div`
  margin-top: 140px;
  padding: 80px 40px;
  border-radius: 32px;
  text-align: center;

  background: linear-gradient(
    180deg,
    rgba(59, 130, 246, 0.18),
    rgba(59, 130, 246, 0.05)
  );
  border: 1px solid rgba(59, 130, 246, 0.35);
`;

const CTATitle = styled.h2`
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 900;
`;

const CTAText = styled.p`
  max-width: 700px;
  margin: 18px auto 36px;
  font-size: 18px;
  line-height: 1.7;
  opacity: 0.85;
`;

const CTAButton = styled.button`
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 800;
  border-radius: 14px;
  border: none;
  cursor: pointer;

  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  color: white;
`;

/* ================= DATA ================= */

const STACKS = [
  {
    icon: Smartphone,
    title: "Mobile Applications",
    color: "linear-gradient(135deg,#3b82f6,#60a5fa)",
    sections: {
      Frontend: ["React Native", "Flutter", "Swift", "Kotlin"],
      Backend: ["Node.js", "Firebase", "Supabase", "Django"],
      Database: ["Firestore", "PostgreSQL", "SQLite"],
      UseCases: ["Consumer Apps", "Startup MVPs", "Enterprise Apps"],
    },
  },
  {
    icon: Globe,
    title: "Web Applications",
    color: "linear-gradient(135deg,#06b6d4,#22d3ee)",
    sections: {
      Frontend: ["React", "Next.js", "Vue", "Angular"],
      Styling: ["CSS", "Tailwind", "Styled Components"],
      Backend: ["Node.js", "NestJS", "Laravel"],
      Deployment: ["Vercel", "Netlify", "AWS"],
    },
  },
  {
    icon: Tv,
    title: "TV Applications",
    color: "linear-gradient(135deg,#8b5cf6,#a78bfa)",
    sections: {
      Platforms: ["Android TV", "tvOS", "Tizen", "WebOS"],
      Languages: ["Java", "Kotlin", "Swift", "JavaScript"],
    },
  },
  {
    icon: Monitor,
    title: "Desktop Applications",
    color: "linear-gradient(135deg,#22c55e,#4ade80)",
    sections: {
      CrossPlatform: ["Electron", "Tauri", "Qt"],
      Native: ["C#", "Swift", "C++"],
    },
  },
  {
    icon: Watch,
    title: "Watch Applications",
    color: "linear-gradient(135deg,#f59e0b,#fbbf24)",
    sections: {
      Platforms: ["watchOS", "Wear OS"],
      Focus: ["Low Power", "Health APIs", "Minimal UI"],
    },
  },
  {
    icon: Cpu,
    title: "Embedded & IoT",
    color: "linear-gradient(135deg,#ef4444,#f87171)",
    sections: {
      Languages: ["C", "C++", "MicroPython"],
      Hardware: ["Arduino", "ESP32", "Raspberry Pi"],
    },
  },
  {
    icon: Puzzle,
    title: "Browser Extensions",
    color: "linear-gradient(135deg,#0ea5e9,#38bdf8)",
    sections: {
      Frontend: ["JavaScript", "HTML", "CSS"],
      APIs: ["Chrome Extensions API", "WebExtensions"],
    },
  },
  {
    icon: Server,
    title: "Backend & APIs",
    color: "linear-gradient(135deg,#6366f1,#818cf8)",
    sections: {
      Languages: ["Node.js", "Java", "Python", "Go"],
      Responsibilities: ["Auth", "APIs", "Business Logic"],
    },
  },
];

/* ================= COMPONENT ================= */

export default function TechnologyLandscapePage() {
  const navigate = useNavigate();
  return (
    <Page>
      <Container>
        <Heading>Technology Landscape for Product Development</Heading>

        <Sub>
          One screen. All platforms. Clear understanding of which technologies
          are used to build different kinds of products.
        </Sub>

        <Grid>
          {STACKS.map((stack, i) => {
            const Icon = stack.icon;

            return (
              <Card
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Header>
                  <IconWrap color={stack.color}>
                    <Icon size={26} />
                  </IconWrap>
                  <Title>{stack.title}</Title>
                </Header>

                {Object.entries(stack.sections).map(([label, items]) => (
                  <Section key={label}>
                    <Label>{label}</Label>

                    <TechList>
                      {items.map((item) => (
                        <TechItem key={item}>
                          <TechIcon>{item[0]}</TechIcon>
                          {item}
                        </TechItem>
                      ))}
                    </TechList>
                  </Section>
                ))}
              </Card>
            );
          })}
        </Grid>
      </Container>
      <CTA>
        <CTATitle>Confused where to start?</CTATitle>

        <CTAText>
          Now that you understand the technology landscape, let us build a{" "}
          <b>clear, step-by-step roadmap</b> based on your goals, experience,
          and timeline.
        </CTAText>

        <CTAButton onClick={() => navigate("/create-roadmap")}>
          Build My Personalized Roadmap →
        </CTAButton>
      </CTA>
    </Page>
  );
}
